import { Autocomplete } from 'stimulus-autocomplete'
import { turboRequest } from '../../helpers/turbo_helper'

// the original optionSelector is incompatible with css grid, which is needed for accessibily navigating options
const optionSelector = ".autocomplete-grid-row[role='row']:not(.autocomplete-helper-row)"

export default class CustomAutocomplete extends Autocomplete {
  static targets = [
    'selectedMenuItemContainer',
    'menuItemSelectionTurboFrame',
    'extraListContainer',
    'extendedListMessage',
    'spinner',
    'inputWrapper',
    'addNewItemContainer',
    'output'
  ]
  static values = {
    extendListOptions: Object,
    selectMenuItemUrl: String,
    selectNewClientPath: String,
    selectNewClientModalSelector: String,
    hideAddNewItemButton: Boolean,
    autocompleteParameters: Object,
    ransackSearchMatcher: String,
    uniqueId: String
  }

  connect() {
    super.connect()
    this.currentUrl = this.urlValue

    this.displayItemSelection = this.displayItemSelection.bind(this)
    this.showLoadingSpinner = this.showLoadingSpinner.bind(this)
    this.hideLoadingSpinner = this.hideLoadingSpinner.bind(this)

    this.element.addEventListener('autocomplete.change', this.displayItemSelection)
    this.element.addEventListener('loadstart', this.showLoadingSpinner)
    this.element.addEventListener('loadend', this.hideLoadingSpinner)
    this.hideLoadingSpinner()
  }

  disconnect() {
    this.element.removeEventListener('autocomplete.change', this.displayItemSelection)
    this.element.removeEventListener('loadstart', this.showLoadingSpinner)
    this.element.removeEventListener('loadend', this.hideLoadingSpinner)
  }

  // override vendor open/close to use the Popover API; the menu is promoted to
  // the top layer and CSS anchor positioning keeps it aligned to the input.
  open() {
    if (this.resultsShown) return
    this.resultsTarget.showPopover()
    this.element.setAttribute('aria-expanded', 'true')
    this.element.dispatchEvent(new CustomEvent('toggle', {
      detail: { action: 'open', inputTarget: this.inputTarget, resultsTarget: this.resultsTarget }
    }))
  }

  close() {
    if (!this.resultsShown) return
    this.resultsTarget.hidePopover()
    this.inputTarget.removeAttribute('aria-activedescendant')
    this.element.setAttribute('aria-expanded', 'false')
    this.element.dispatchEvent(new CustomEvent('toggle', {
      detail: { action: 'close', inputTarget: this.inputTarget, resultsTarget: this.resultsTarget }
    }))
  }

  get resultsShown() {
    return this.resultsTarget.matches(':popover-open')
  }

  showLoadingSpinner() {
    this.spinnerTarget.classList.remove('d-none')
  }

  hideLoadingSpinner() {
    this.spinnerTarget.classList.add('d-none')
  }

  displayItemSelection() {
    if (['', null].includes(this.selectMenuItemUrlValue)) {
      return
    }

    this.selectedMenuItemContainerTarget.classList.remove('d-none')
    this.inputWrapperTarget.classList.add('d-none')

    var url = new URL(this.selectMenuItemUrlValue)
    var params = new URLSearchParams(url.search)
    params.append('item_id', this.hiddenTarget.value)

    if (this.uniqueIdValue) {
      params.append('unique_id', this.uniqueIdValue)
    }

    turboRequest(`${url.pathname}?${params}`)

    this.element.dispatchEvent(
      new CustomEvent('typeahead:changed', { bubbles: true, detail: { id: this.uniqueIdValue } })
    )
    this.outputTarget.textContent = 'Option selected'
  }

  // overrides the vendor buildURL method
  buildURL(query) {
    const url = new URL(this.currentUrl, window.location.href)
    url.searchParams.set(`q[${this.ransackSearchMatcherValue}]`, query)
    url.searchParams.set('query_string', query)
    if (this.hasAutocompleteParametersValue) {
      Object.keys(this.autocompleteParametersValue).forEach(key =>
        url.searchParams.set(key, this.autocompleteParametersValue[key])
      )
    }
    return url.href
  }

  // overrides the vendor replaceResults method in order to call handleResultsAnnouncement for accessibility
  replaceResults(html) {
    this.resultsTarget.innerHTML = html
    this.identifyOptions()
    if (!!this.options) {
      this.open()
    } else {
      this.close()
    }
    // only change from vendor method
    this.handleResultsAnnouncement()
  }

  newClient() {
    if (this.selectNewClientPathValue) {
      turboRequest(this.appendFullNameToUrl(this.selectNewClientPathValue))
    } else if (this.selectNewClientModalSelectorValue) {
      const targetModal = document.querySelector(this.selectNewClientModalSelectorValue)
      if (targetModal == null) {
        throw new Error(`Could not find target modal with selector '${this.selectNewClientModalSelectorValue}'!`)
      }

      const frame = targetModal.querySelector('turbo-frame')
      if (frame != null) {
        const originalSrc = frame.getAttribute('src')
        frame.setAttribute('src', this.appendFullNameToUrl(originalSrc))
      }

      targetModal.dispatchEvent(new CustomEvent('modal:open'))
    }
    this.close()
  }

  appendFullNameToUrl = (url) => {
    const [path, queryString] = url.split('?')
    const urlParams = new URLSearchParams(queryString)
    urlParams.append('full_name', this.inputTarget.value)

    return `${path}?${urlParams}`
  }

  switchUrl() {
    if (this.currentUrl == this.urlValue) {
      this.currentUrl = this.extendListOptionsValue.extended_path
    } else {
      this.currentUrl = this.urlValue
    }

    this.onInputChange()
  }

  extraListContainerTargetConnected(element) {
    if (Object.keys(this.extendListOptionsValue).length === 0) {
      element.remove()
    } else {
      if (this.currentUrl == this.urlValue) {
        this.extendedListMessageTarget.innerHTML = this.extendListOptionsValue.extended_message
      } else {
        this.extendedListMessageTarget.innerHTML = this.extendListOptionsValue.revert_extended_message
      }
    }
  }

  addNewItemContainerTargetConnected(element) {
    if (this.hideAddNewItemButtonValue) {
      element.remove()
    }
  }

  hiddenTargetConnected(element) {
    if (element.value) {
      this.displayItemSelection()
    }
  }

  clear() {
    super.clear()
    this.selectedMenuItemContainerTarget.classList.add('d-none')
    this.inputWrapperTarget.classList.remove('d-none')
    this.menuItemSelectionTurboFrameTarget.innerHTML = ''
    this.outputTarget.textContent = 'Cleared'

    this.element.dispatchEvent(
      new CustomEvent('autocomplete.clear', { bubbles: true, detail: { id: this.uniqueIdValue } })
    )
  }

  isDisabled(element) {
    return element.getAttribute('aria-disabled') === 'true' || element.getAttribute('data-autocomplete-disabled') === 'true'
  }

  // overrides the vendor hideAndRemoveOptions method
  hideAndRemoveOptions() {
    this.close()
  }

  // overrides the vendor commit method
  commit(selected) {
    if (this.isDisabled(selected)) return

    if (selected instanceof HTMLAnchorElement) {
      selected.click()
      this.close()
      return
    }

    const textValue = selected.getAttribute('data-autocomplete-label') || selected.textContent.trim()
    const value = selected.getAttribute('data-autocomplete-value') || textValue
    this.inputTarget.value = textValue

    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = value
      this.hiddenTarget.dispatchEvent(new Event('input'))
      this.hiddenTarget.dispatchEvent(new Event('change'))
    } else {
      this.inputTarget.value = value
    }

    this.inputTarget.focus()
    this.hideAndRemoveOptions()

    this.element.dispatchEvent(
      new CustomEvent('autocomplete.change', {
        bubbles: true,
        detail: { value: value, textValue: textValue, selected: selected, id: this.uniqueIdValue }
      })
    )
  }

  handleResultsAnnouncement() {
    let selectableOptions = this.options.filter(option => option.classList.contains('autocomplete-result-option')).length
    let text = selectableOptions > 1 ? "results" : "result";
    if (selectableOptions > 0) {
      this.outputTarget.textContent = `${selectableOptions} ${text} found`
    } else {
      this.outputTarget.textContent = 'No results found'
    }
  }

  // methods below are duplicated from the vendor to use the overrided optionSelector
  identifyOptions() {
    const prefix = this.resultsTarget.id || "stimulus-autocomplete"
    const optionsWithoutId = this.resultsTarget.querySelectorAll(`${optionSelector}:not([id])`)
    optionsWithoutId.forEach(el => el.id = `${prefix}-option-${Autocomplete.uniqOptionId++}`)
  }

  get options() {
    return Array.from(this.resultsTarget.querySelectorAll(optionSelector))
  }

  onResultsClick = (event) => {
    if (!(event.target instanceof Element)) return
    const selected = event.target.closest(optionSelector)
    if (selected && !this.isDisabled(selected)) this.commit(selected)
  }

  // dupllication of vender onEnterKeydown method
  onEnterKeydown = (event) => {
    const selected = this.selectedOption
    if (selected && this.resultsShown) {
      // triggers the new client method if the option is triggered via keydown and not mouse click
      // usually we would favor native elements to handle onclick AND keydown, but that's impractical with vendor library
      if (selected.id === 'add-new-item') {
        this.newClient()
        event.preventDefault()
      }

      this.commit(selected)
      if (!this.hasSubmitOnEnterValue) {
        event.preventDefault()
      }
    }
  }
}
